import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Users, Target, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="overline mb-4 block">About Us</span>
              <h1 className="text-3xl md:text-4xl font-light tracking-tight text-[#1A1A2E] leading-[1.1] mb-6" data-testid="about-title">
                1031 Exchange Property Specialists
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                1031Condo.com is operated by Krieger Real Estate LLC, a licensed real estate brokerage focused on helping clients identify and acquire individual condo units suitable for 1031 exchanges.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHBlb3BsZSUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWwlMjBhcmNoaXRlY3QlMjBvZmZpY2UlMjBkaXZlcnNlfGVufDB8fHx8MTc3MzAyMjA5N3ww&ixlib=rb-4.1.0&q=85"
                alt="Professional team meeting"
                className="w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="overline mb-4 block">Our Mission</span>
              <h2 className="text-2xl md:text-3xl font-light tracking-tight text-[#1A1A2E] leading-tight">
                Simplifying 1031 Exchanges
              </h2>
            </motion.div>

            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Our team focuses on sourcing condominium units located in major gateway markets such as New York City and Miami. These are established, high-volume markets with broad buyer pools and diversified economies.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We understand the challenges buyers face when executing a 1031 exchange, particularly the pressure of identifying suitable replacement properties within strict IRS timelines. Our website is designed to streamline this process by providing a curated selection of individual condo units.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline mb-4 block">Our Approach</span>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-[#1A1A2E] leading-tight">
              What Sets Us Apart
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                icon: Target, 
                title: "Market Focus", 
                desc: "We concentrate exclusively on NYC and Miami — markets we know deeply and understand completely." 
              },
              { 
                icon: Building2, 
                title: "Quality Standards", 
                desc: "Every property is reviewed based on pricing, building quality, and ownership considerations." 
              },
              { 
                icon: Users, 
                title: "Collaborative Process", 
                desc: "We work closely with buyers, QIs, attorneys, and tax advisors to ensure smooth transactions." 
              },
              { 
                icon: Award, 
                title: "Experience", 
                desc: "Decades of combined experience in luxury real estate investment and 1031 exchange transactions." 
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                className="p-8 bg-white border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <item.icon className="w-10 h-10 text-[#0066FF] mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-medium text-[#1A1A2E] mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="overline mb-4 block">Our Clients</span>
              <h2 className="text-2xl md:text-3xl font-light tracking-tight text-[#1A1A2E] leading-tight mb-6">
                Who We Work With
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                1031Condo.com serves real estate owners looking to identify and acquire individual condo unit replacement properties through tax-deferred 1031 exchanges.
              </p>

              <ul className="space-y-4">
                {[
                  "Property owners selling real estate and seeking replacement properties",
                  "Owners of multifamily buildings seeking to transition property types",
                  "Clients seeking geographic diversification into NYC or Miami",
                  "Real estate owners looking for simplified ownership structures",
                  "Clients on a 45-day identification deadline needing available listings",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#0066FF] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-6"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1673563932832-a0c9e0ed26f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBiZWlnZSUyMHdoaXRlfGVufDB8fHx8MTc3MzAyMjA5Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Luxury interior"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[3/4] overflow-hidden mt-12">
                <img
                  src="https://images.unsplash.com/photo-1705326701287-346fc37a2c86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBiZWlnZSUyMHdoaXRlfGVufDB8fHx8MTc3MzAyMjA5Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Interior detail"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline mb-4 block">Our Network</span>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-[#1A1A2E] leading-tight mb-6">
              We Work Closely With
            </h2>
            <p className="text-lg text-slate-600">
              Our collaborative approach involves working with a network of professionals to ensure seamless transactions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              "1031 Exchange Buyers",
              "Qualified Intermediaries",
              "Real Estate Attorneys",
              "Tax Advisors & CPAs",
            ].map((partner, idx) => (
              <motion.div
                key={idx}
                className="p-8 bg-white border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <p className="text-lg font-medium text-[#1A1A2E]">{partner}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Buyer Representation */}
      <section className="py-24 bg-slate-50" data-testid="buyer-representation">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-semibold text-[#0066FF] uppercase tracking-wider mb-3 block">Buyer Representation</span>
              <h2 className="text-2xl md:text-3xl font-light text-[#1A1A2E] leading-tight mb-6">
                We Represent You as the Buyer
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Krieger Real Estate LLC is a licensed real estate brokerage representing buyers in the acquisition of individual condominium units. We act as your buyer's agent and advocate throughout the transaction.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We present offers and handle negotiations with the sponsor, developer, or bulk condo unit owner on your behalf — working to secure favorable pricing, terms, and concessions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-[#1A1A2E] mb-4 text-lg">Our Brokerage Services</h3>
                <ul className="space-y-3">
                  {[
                    "Property identification and search",
                    "Offer preparation and presentation",
                    "Negotiation with sponsors, developers, or unit owners",
                    "Transaction coordination through closing",
                  ].map((item, i) => (
                    <li key={item} className="flex items-start gap-3 text-slate-600">
                      <div className="w-5 h-5 bg-[#0066FF]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-[#0066FF] rounded-full" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-[#1A1A2E] mb-2 text-sm">New York</h4>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Krieger Real Estate LLC represents buyers in most transactions. In limited off-market resale situations, dual agency, if applicable, will be disclosed in writing and requires informed written consent from all parties in accordance with New York law. A designated agent will be appointed where required.
                  </p>
                </div>
                <div className="bg-white p-5 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-[#1A1A2E] mb-2 text-sm">Florida</h4>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Krieger Real Estate LLC operates as a Transaction Broker in Florida, representing buyers. We do not represent sellers or enter into dual agency relationships in Florida.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brokerage Clarification */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-semibold text-[#0066FF] uppercase tracking-wider mb-3 block">Our Role</span>
            <h2 className="text-2xl md:text-3xl font-light text-[#1A1A2E] leading-tight mb-6">
              Licensed Real Estate Brokerage Services
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              1031Condo.com is a real estate brokerage website that assists clients in identifying and acquiring individual condo units suitable for 1031 exchanges. Our role is to assist clients in sourcing and transacting real estate through brokerage services.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Krieger Real Estate LLC acts solely as a real estate broker representing buyers and/or sellers in property transactions. We do not sponsor, syndicate, or manage real estate offerings of any kind.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              This website operates independently from any private funds or securities offerings. Krieger Real Estate LLC provides real estate brokerage services only.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="bg-[#1A1A2E] px-6 lg:px-12 pt-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs text-slate-500 leading-relaxed text-center" data-testid="about-disclaimer">
            Krieger Real Estate LLC is a licensed real estate brokerage. This website is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities. We do not provide investment, legal, or tax advice. All real estate transactions involve direct property ownership. All real estate purchases involve risk. Buyers should conduct independent due diligence.
          </p>
        </div>
      </div>

      {/* CTA */}
      <section className="py-24 bg-[#1A1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-light text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Whether you're planning a 1031 exchange or exploring strategies to reposition your real estate portfolio, our team is here to help.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#1A1A2E] hover:bg-slate-100 px-8 py-6 text-base rounded-sm">
                Contact Our Team
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
